import React, { useEffect, useState } from "react";
import { FaUser, FaTrash, FaExclamationTriangle } from "react-icons/fa";
import { MdPersonAdd } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { db, auth } from "../firebase";
import { collection, getDocs, doc, deleteDoc, query, orderBy } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";

const packageMapping = {
	1: "Free",
	2: "Monthly",
	3: "Yearly",
};

const UserManagement = () => {
	const navigate = useNavigate();
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [error, setError] = useState(null);
	const [deleteModal, setDeleteModal] = useState({ show: false, userId: null, userName: "" });
	const [isDeleting, setIsDeleting] = useState(false);

	const fetchUsers = async () => {
		setLoading(true);
		setError(null);
		console.log("🚀 STARTING FETCH FROM COLLECTION 'users'...");

		try {
			const querySnapshot = await getDocs(collection(db, "users"));
			console.log("✅ SNAPSHOT SUCCESS! Docs count:", querySnapshot.size);

			const userList = querySnapshot.docs.map(doc => {
				const data = doc.data();
				return {
					id: doc.id,
					...data,
					packageName: data.planName || packageMapping[data.plan] || "Basic",
				};
			});

			userList.sort((a, b) => {
				const parseDate = (val) => {
					if (!val) return new Date(0);
					if (val.toDate) return val.toDate();
					if (typeof val === 'string') {
						// Handle "January 16, 2026 at 1:11:47 AM UTC+5"
						const cleaned = val.replace(/\s+at\s+/i, ' ').replace(/UTC\+(\d+)$/i, 'UTC+0$100');
						const d = new Date(cleaned);
						if (!isNaN(d.getTime())) return d;
					}
					const d = new Date(val);
					return isNaN(d.getTime()) ? new Date(0) : d;
				};
				return parseDate(b.createdAt) - parseDate(a.createdAt);
			});

			setUsers(userList);
		} catch (err) {
			console.error("❌ FIREBASE ERROR:", err);
			if (err.code === 'permission-denied') {
				setError("PERMISSION DENIED: Firebase rules are blocking the read. Set 'allow read: if true;' in rules to test.");
			} else {
				setError(err.message);
			}
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, []);

	const handleDeleteClick = (user) => {
		setDeleteModal({ show: true, userId: user.id, userName: user.email || user.id });
	};

	const confirmDelete = async () => {
		if (!deleteModal.userId) return;
		setIsDeleting(true);
		try {
			await deleteDoc(doc(db, "users", deleteModal.userId));
			setDeleteModal({ show: false, userId: null, userName: "" });
			await fetchUsers(); // Reload user data
		} catch (error) {
			console.error("Error deleting user:", error);
			alert("Failed to delete user. Please try again.");
		} finally {
			setIsDeleting(false);
		}
	};

	const formatCreated = (timestamp) => {
		if (!timestamp) return <span className="text-gray-400 italic">Not set</span>;

		let date;
		if (timestamp.toDate) {
			date = timestamp.toDate();
		} else if (typeof timestamp === 'string') {
			// Handle format: "January 16, 2026 at 1:11:47 AM UTC+5"
			// 1. Remove the " at "
			// 2. Fix the "UTC+5" to "UTC+0500" for better compatibility
			const cleaned = timestamp.replace(/\s+at\s+/i, ' ').replace(/UTC\+(\d+)$/i, 'UTC+0$100');
			date = new Date(cleaned);

			// If still invalid and it's a numeric string, try parsing as number
			if (isNaN(date.getTime()) && /^\d+$/.test(timestamp)) {
				date = new Date(Number(timestamp));
			}
		} else {
			date = new Date(timestamp);
		}

		if (isNaN(date.getTime())) {
			return (
				<div className="flex flex-col items-center">
					<span className="text-[10px] text-red-500 font-mono break-all max-w-[120px]">
						{String(timestamp)}
					</span>
					<span className="text-[9px] text-gray-400 italic font-bold uppercase">Format Not Recognized</span>
				</div>
			);
		}

		return (
			<div className="flex flex-col items-center">
				<span className="font-medium text-gray-700">
					{date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
				</span>
				<span className="text-[10px] text-gray-400">
					{date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
				</span>
			</div>
		);
	};

	const filteredUsers = users.filter(user =>
		(user.email || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
		(user.id || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
		(user.name || "").toLowerCase().includes(searchTerm.toLowerCase())
	);

	return (
		<div className="min-h-screen flex flex-col py-4 px-2 sm:px-4 md:px-8">
			<div className="flex justify-between items-center mb-4">
				<h2 className="text-2xl font-bold text-gray-800">User Management</h2>
				<div className="flex items-center gap-3">
					<span className={`px-3 py-1 rounded-full text-xs font-bold ${loading ? 'bg-blue-100 text-blue-700' : users.length > 0 ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
						{loading ? "Connecting to Firebase..." : `${users.length} Records Found`}
					</span>
					{/* <button
						onClick={() => { localStorage.clear(); window.location.reload(); }}
						className="text-xs text-gray-400 hover:text-red-500 underline"
					>
						Force Reset
					</button> */}
				</div>
			</div>

			{error && (
				<div className="mb-6 p-4 bg-red-50 border-2 border-red-200 text-red-700 rounded-xl text-sm font-bold animate-pulse">
					⚠️ {error}
				</div>
			)}
			<div className="w-full max-w-full mx-auto">
				<div className="mb-4 flex flex-col sm:flex-row items-stretch sm:items-start justify-between gap-2 px-0 sm:px-2">
					<input
						type="text"
						placeholder="Search by email or UID.."
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="w-full sm:max-w-md px-4 py-2 rounded-xl border border-orange focus:outline-none focus:ring-2 focus:ring-orange2 bg-white text-gray-700 shadow-sm text-xs sm:text-sm md:text-base"
					/>
					<div className="flex gap-2 w-full sm:w-auto mt-2 sm:mt-0">
						<button
							className="bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-4 rounded-2xl text-xs sm:text-sm font-bold shadow-sm transition-all border border-gray-300"
							onClick={() => window.location.reload()}
						>
							🔄 Sync Latest Data
						</button>
						{/* <button className="bg-orange-gradient from-orange to-orange2 py-2 px-6 rounded-2xl text-white flex-1 sm:flex-none text-xs sm:text-sm md:text-base font-bold shadow-md transition-all hover:from-orange2 hover:to-orange focus:outline-none border-0"
							onClick={() => navigate("/add-user")}
						>
							<MdPersonAdd className="inline-block mr-2 mb-0.5 text-lg align-text-bottom" /> Add New User
						</button> */}
					</div>
				</div>

				<div className="bg-white rounded-2xl shadow-xl w-full">
					<div className="overflow-x-auto scrollbar-thin scrollbar-thumb-orange/30 scrollbar-track-orange/10 rounded-2xl">
						<table className="w-full text-xs sm:text-sm min-w-[600px] md:min-w-[900px]">
							<thead>
								<tr className="bg-gradient-to-r from-orange to-yellow text-white">
									<th className="py-3 px-2 text-center font-bold min-w-[32px] w-8">ID</th>
									<th className="py-3 px-2 text-center font-bold min-w-[160px]">EMAIL / UID</th>
									<th className="py-3 px-2 text-center font-bold min-w-[140px]">CREATED</th>
									<th className="py-3 px-2 text-center font-bold min-w-[160px]">PACKAGE TYPE</th>
									<th className="py-3 px-2 text-center font-bold min-w-[90px]">ACTIONS</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr><td className="py-6 text-center" colSpan={5}>Loading users...</td></tr>
								) : error ? (
									<tr><td className="py-6 text-center text-red-500 font-bold" colSpan={5}>{error}</td></tr>
								) : filteredUsers.length === 0 ? (
									<tr><td className="py-6 text-center" colSpan={5}>No users found in collection "users"</td></tr>
								) : filteredUsers.map((user, index) => (
									<tr
										key={user.id}
										className={`last:border-b-0 group bg-white border-b text-center border-gray-100 transition-colors hover:bg-gray-50`}
									>
										<td className="py-4 px-2 align-middle min-w-[32px] w-8 font-bold text-gray-400">
											{index + 1}
										</td>
										<td className="py-4 px-2 align-middle text-gray-800 text-xs sm:text-sm min-w-[160px] break-words truncate">
											<div className="flex flex-col items-center">
												<span className="font-semibold text-gray-900">{user.email || "No Email"}</span>
												<span className="text-[10px] text-gray-400 font-mono">{user.id}</span>
											</div>
										</td>
										<td className="py-4 px-2 align-middle text-gray-800 text-xs sm:text-sm min-w-[140px]">{formatCreated(user.createdAt)}</td>
										<td className="py-4 px-2 align-middle min-w-[160px]">
											<span className={`px-3 py-1 rounded-full text-xs font-bold ${user.plan === 3 ? 'bg-yellow-100 text-yellow-700' :
												user.plan === 2 ? 'bg-blue-100 text-blue-700' :
													'bg-gray-100 text-gray-700'
												}`}>
												{user.packageName}
											</span>
										</td>
										<td className="py-4 px-2 align-middle min-w-[90px]">
											<div className="flex gap-2 justify-center">
												<button
													className="px-3 py-1 rounded-xl bg-gradient-to-r from-red-500 to-orange-500 text-white font-bold shadow-md border-0 hover:from-orange-500 hover:to-red-500 transition-all text-xs sm:text-sm md:text-base flex items-center gap-1"
													onClick={() => handleDeleteClick(user)}
												>
													<FaTrash size={12} /> Delete
												</button>
											</div>
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Delete Confirmation Modal */}
			{deleteModal.show && (
				<div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm animate-fadeIn">
					<div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-sm transform transition-all animate-scaleUp">
						<div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
							<FaExclamationTriangle className="text-3xl text-red-600" />
						</div>
						<h3 className="text-xl font-bold text-center text-gray-900 mb-2">Delete User?</h3>
						<p className="text-center text-gray-600 mb-6">
							Are you sure you want to delete <span className="font-semibold text-gray-900">"{deleteModal.userName}"</span>? This action cannot be undone.
						</p>
						<div className="flex gap-3">
							<button
								onClick={() => setDeleteModal({ show: false, userId: null, userName: "" })}
								className="flex-1 py-3 px-4 rounded-xl border border-gray-300 text-gray-700 font-bold hover:bg-gray-50 transition-colors"
							>
								Cancel
							</button>
							<button
								onClick={confirmDelete}
								disabled={isDeleting}
								className={`flex-1 py-3 px-4 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700 transition-colors shadow-lg shadow-red-200 flex items-center justify-center gap-2 ${isDeleting ? 'opacity-70 cursor-not-allowed' : ''}`}
							>
								{isDeleting ? (
									<>
										<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
										Deleting...
									</>
								) : (
									"Delete"
								)}
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default UserManagement;
